import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AuthContext } from '../AuthContext';
import { fetchDays, listRequests, setHandled, deleteRequest } from './inboxApi';
import { formatDate, formatDateTime, todayBrussels } from './requestFormat';
import {
  dayKey,
  defaultOpenKeys,
  hasHiddenMonths,
  monthKey,
  shownMonths,
  treeForStatus,
  treeKeys,
  visibleDates,
  yearKey,
} from './requestGrouping';
import './RequestDayList.css';

// DJB-279: the request tree shared by Song Requests and Contact Requests —
// year › month › day, newest first, built from GET /webforms/{kind}/days
// (the per-location groups of DJB-265 are gone; the location is on the
// card). A day's cards are only loaded once they are on screen, i.e. the
// day, its month and its year are all open. Defaults (requestGrouping.js):
// the current year and month start open, days with open requests start
// open, fully completed days start collapsed. Every year lists its three
// most recent months until "Show all ›".
//
// The filter (To Do / Completed / All) decides which cards a day loads
// (status=open / handled / all) and hides the days that have nothing for it;
// the counts on the headers always come from the full day summary.
//
// - `renderCard(item)` returns { content, aside } — the card text and the
//   status box on the right of an open card (the match panel for songs). A
//   completed card always shows the Completed box instead.
// - `today` is only overridden by tests; it defaults to the Brussels day.

const EMPTY_SLICE = { items: [], total: 0, page: 0, loading: false, error: false, loaded: false };

const Chevron = () => <span className="request-chevron" aria-hidden="true" />;

const CheckIcon = ({ className = '' }) => (
  <svg className={`request-check-icon ${className}`} viewBox="0 0 20 20" aria-hidden="true" focusable="false">
    <circle cx="10" cy="10" r="9" fill="currentColor" />
    <path d="M6 10.5l2.5 2.5L14 7.5" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// The status box of a completed card: ✓ "Completed." + who and when
export const CompletedStatus = ({ item }) => (
  <div className="request-status request-status-completed" data-testid="request-status">
    <CheckIcon className="request-status-icon" />
    <span className="request-status-heading">Completed.</span>
    <span className="request-status-meta">
      <span>
        Completed by <strong>{item.handledBy || 'unknown'}</strong>
      </span>
      <span>{formatDateTime(item.handledAt)}</span>
    </span>
  </div>
);

// One request: the card (content + status box) with "Mark as Done" / "Mark as
// To Do" and "Delete Request" to its right. A completed card is green and
// its status box is always the Completed box.
export const RequestCard = ({ item, content, aside, onToggleHandled, onDelete }) => {
  const done = Boolean(item.handledAt);
  const status = done ? <CompletedStatus item={item} /> : aside;
  return (
    <div className={`request-card-row${done ? ' handled' : ''}`} data-testid="request-card">
      <div className="request-card">
        <div className="request-card-main">{content}</div>
        {status && <div className="request-card-status">{status}</div>}
      </div>
      <div className="request-card-actions">
        <button
          type="button"
          className={`request-toggle-done ${done ? 'to-todo' : 'to-done'}`}
          onClick={() => onToggleHandled(item)}
        >
          {done ? (
            <>
              Mark as <strong>To Do</strong>
            </>
          ) : (
            <>
              Mark as <strong>Done</strong>
            </>
          )}
        </button>
        <button type="button" className="request-delete" onClick={() => onDelete(item)}>
          Delete Request
        </button>
      </div>
    </div>
  );
};

const SliceView = ({ slice, emptyText, renderCard, onToggleHandled, onDelete, onLoadMore }) => {
  if (!slice) return null;
  return (
    <>
      {slice.error && <div className="request-note request-error">Could not load requests.</div>}
      {slice.loading && slice.items.length === 0 && !slice.error && <div className="request-note">Loading…</div>}
      {slice.loaded && !slice.loading && slice.items.length === 0 && (
        <div className="request-note">{emptyText}</div>
      )}
      {slice.items.map((item) => {
        const { content, aside } = renderCard(item);
        return (
          <RequestCard
            key={item.id}
            item={item}
            content={content}
            aside={aside}
            onToggleHandled={onToggleHandled}
            onDelete={onDelete}
          />
        );
      })}
      {slice.items.length > 0 && slice.items.length < slice.total && (
        <button type="button" className="request-load-more" disabled={slice.loading} onClick={onLoadMore}>
          Load more
        </button>
      )}
    </>
  );
};

const RequestDayList = ({ kind, status = 'open', onChanged, today, renderCard }) => {
  const { authData } = useContext(AuthContext) || {};
  const accessToken = authData?.accessToken;
  const todayKey = today || todayBrussels();

  const [days, setDays] = useState(null);
  const [daysError, setDaysError] = useState(false);
  // open: the year/month/day keys that are expanded; showAll: the years
  // whose "Show all ›" was clicked
  const [open, setOpen] = useState(() => new Set());
  const [showAll, setShowAll] = useState(() => new Set());
  const [slices, setSlices] = useState({});
  const [actionError, setActionError] = useState(false);

  // Refs so in-flight work always sees the current filter / expansion state
  const statusRef = useRef(status);
  statusRef.current = status;
  const openRef = useRef(open);
  openRef.current = open;
  // Keys that already got their default; a later refresh of the day summary
  // leaves the user's (or the default's) expansion state alone
  const decided = useRef(new Set());
  // Bumped whenever kind/filter/token change: responses from before are dropped
  const generation = useRef(0);
  const latestDays = useRef(0);
  const latestSlice = useRef({});

  const refreshDays = useCallback(async () => {
    const gen = generation.current;
    const id = ++latestDays.current;
    try {
      const list = await fetchDays(kind, accessToken);
      if (id !== latestDays.current || gen !== generation.current) return null;
      const summary = Array.isArray(list) ? list : [];
      const defaults = defaultOpenKeys(summary, todayKey);
      const next = new Set(openRef.current);
      let changed = false;
      treeKeys(summary).forEach((key) => {
        if (decided.current.has(key)) return;
        decided.current.add(key);
        changed = true;
        if (defaults.has(key)) next.add(key);
      });
      if (changed) {
        openRef.current = next;
        setOpen(next);
      }
      setDays(summary);
      setDaysError(false);
      return summary;
    } catch (e) {
      if (id !== latestDays.current || gen !== generation.current) return null;
      console.error('Failed to load request days:', e);
      setDaysError(true);
      return null;
    }
  }, [kind, accessToken, todayKey]);

  const loadSlice = useCallback(
    async (date, listStatus, page) => {
      const id = (latestSlice.current[date] = (latestSlice.current[date] || 0) + 1);
      const gen = generation.current;
      const isCurrent = () => id === latestSlice.current[date] && gen === generation.current;
      setSlices((prev) => ({ ...prev, [date]: { ...(prev[date] || EMPTY_SLICE), loading: true, error: false } }));
      try {
        const res = await listRequests(kind, listStatus, page, accessToken, { date });
        if (!isCurrent()) return;
        setSlices((prev) => {
          const cur = prev[date] || EMPTY_SLICE;
          return {
            ...prev,
            [date]: {
              items: page === 0 ? res.items : [...cur.items, ...res.items],
              total: res.total,
              page,
              loading: false,
              error: false,
              loaded: true,
            },
          };
        });
      } catch (e) {
        if (!isCurrent()) return;
        console.error('Failed to load requests:', e);
        setSlices((prev) => ({ ...prev, [date]: { ...(prev[date] || EMPTY_SLICE), loading: false, error: true } }));
      }
    },
    [kind, accessToken]
  );

  // A new kind / filter / token: drop the loaded cards and re-read the day
  // summary. The expansion state survives a filter switch.
  useEffect(() => {
    generation.current += 1;
    setSlices({});
    setActionError(false);
    refreshDays();
  }, [kind, status, accessToken, refreshDays]);

  const tree = useMemo(() => treeForStatus(days || [], status), [days, status]);
  const visible = useMemo(() => visibleDates(tree, open, showAll), [tree, open, showAll]);
  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  // Whatever is on screen and not loaded yet gets loaded (first render,
  // expanding something, a filter switch, a day summary refresh)
  useEffect(() => {
    visible.forEach((date) => {
      const slice = slices[date];
      if (!slice || (!slice.loaded && !slice.loading && !slice.error)) {
        loadSlice(date, status, 0);
      }
    });
  }, [visible, slices, status, loadSlice]);

  const toggle = (key) => {
    const next = new Set(openRef.current);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    openRef.current = next;
    setOpen(next);
  };

  const showAllMonths = (year) => setShowAll((prev) => new Set([...prev, year]));

  // Always reload after an action (also after a failure: the item may already
  // be gone or changed): the day counts, every day on screen, then the
  // caller's counters (nav badges).
  const afterAction = async () => {
    const reloads = [refreshDays()];
    visibleRef.current.forEach((date) => reloads.push(loadSlice(date, statusRef.current, 0)));
    await Promise.all(reloads);
    if (onChanged) onChanged();
  };

  const toggleHandled = async (item) => {
    try {
      await setHandled(kind, item.id, !item.handledAt, accessToken);
      setActionError(false);
    } catch (e) {
      console.error('Failed to update request:', e);
      setActionError(true);
    }
    await afterAction();
  };

  const remove = async (item) => {
    if (!window.confirm('Delete this request permanently?')) return;
    try {
      await deleteRequest(kind, item.id, accessToken);
      setActionError(false);
    } catch (e) {
      console.error('Failed to delete request:', e);
      setActionError(true);
    }
    await afterAction();
  };

  const emptyText = status === 'open' ? 'Nothing to do.' : 'No requests.';

  const renderDay = (day) => {
    const key = dayKey(day.date);
    const isOpen = open.has(key);
    const hasOpen = day.open > 0;
    const bodyId = `request-day-${kind}-${day.date}`;
    return (
      <section
        key={day.date}
        className={`request-day${isOpen ? ' is-open' : ''}${hasOpen ? ' has-open' : ' is-complete'}`}
        data-testid="request-day"
        data-date={day.date}
      >
        <h4 className="request-day-header">
          <button
            type="button"
            className="request-day-toggle"
            aria-expanded={isOpen}
            aria-controls={bodyId}
            onClick={() => toggle(key)}
          >
            <span className="request-day-marker" aria-hidden="true">
              {hasOpen ? <Chevron /> : <CheckIcon />}
            </span>
            <span className="request-day-date">{formatDate(day.date)}</span>
            {hasOpen && (
              <span className="request-day-badge" data-testid="request-day-badge">
                {day.open}
              </span>
            )}
          </button>
        </h4>
        {isOpen && (
          <div className="request-day-body" id={bodyId}>
            <SliceView
              slice={slices[day.date]}
              emptyText="No requests."
              renderCard={renderCard}
              onToggleHandled={toggleHandled}
              onDelete={remove}
              onLoadMore={() => loadSlice(day.date, status, (slices[day.date]?.page || 0) + 1)}
            />
          </div>
        )}
      </section>
    );
  };

  const renderMonth = (month) => {
    const key = monthKey(month.month);
    const isOpen = open.has(key);
    const bodyId = `request-month-${kind}-${month.month}`;
    const complete = month.total > 0 && month.completed === month.total;
    return (
      <section
        key={month.month}
        className={`request-month${isOpen ? ' is-open' : ''}`}
        data-testid="request-month"
        data-month={month.month}
      >
        <h3 className="request-month-header">
          <button
            type="button"
            className="request-month-toggle"
            aria-expanded={isOpen}
            aria-controls={bodyId}
            onClick={() => toggle(key)}
          >
            <Chevron />
            <span className="request-month-label">{month.label}</span>
            <span
              className={`request-month-progress${complete ? ' is-complete' : ''}`}
              data-testid="request-month-progress"
            >
              {`${month.completed}/${month.total} completed`}
            </span>
          </button>
        </h3>
        {isOpen && (
          <div className="request-month-body" id={bodyId}>
            {month.days.map(renderDay)}
          </div>
        )}
      </section>
    );
  };

  const renderYear = (year) => {
    const key = yearKey(year.year);
    const isOpen = open.has(key);
    const bodyId = `request-year-${kind}-${year.year}`;
    const allShown = showAll.has(year.year);
    return (
      <section
        key={year.year}
        className={`request-year${isOpen ? ' is-open' : ''}`}
        data-testid="request-year"
        data-year={year.year}
      >
        <h2 className="request-year-header">
          <button
            type="button"
            className="request-year-toggle"
            aria-expanded={isOpen}
            aria-controls={bodyId}
            onClick={() => toggle(key)}
          >
            <Chevron />
            <span className="request-year-label">{year.year}</span>
          </button>
        </h2>
        {isOpen && (
          <div className="request-year-body" id={bodyId}>
            {shownMonths(year.months, allShown).map(renderMonth)}
            {hasHiddenMonths(year.months, allShown) && (
              <button type="button" className="request-show-all" onClick={() => showAllMonths(year.year)}>
                Show all <span aria-hidden="true">›</span>
              </button>
            )}
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="request-tree">
      {actionError && (
        <div className="request-note request-error" role="alert">
          Could not update this request. It may have been changed by someone else; the list has been refreshed.
        </div>
      )}
      {daysError && <div className="request-note request-error">Could not load requests.</div>}
      {days === null && !daysError && <div className="request-note">Loading…</div>}
      {days && tree.length === 0 && <div className="request-note">{emptyText}</div>}
      {tree.map(renderYear)}
    </div>
  );
};

export default RequestDayList;
